import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useRef, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {border, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {useCoeditingAPI, useCoeditingState} from "@khan/khanmigo-coediting";

import pencilIcon from "@phosphor-icons/core/regular/pencil-simple.svg";

interface Props {
    toolTitle: string;
    canEdit?: boolean;
    viewOnly?: boolean;
}

export const ToolTitle = ({
    toolTitle = "",
    canEdit = false,
    viewOnly = false,
}: Props) => {
    const {documentTitle} = useCoeditingState();
    const {setDocumentTitle} = useCoeditingAPI();

    const [editing, setEditing] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const displayedText = editing ? documentTitle : documentTitle || toolTitle;

    if (!canEdit) {
        return (
            <View style={styles.container}>
                <LabelLarge style={styles.static}>{displayedText}</LabelLarge>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <View style={styles.sizer} data-text={displayedText}>
                <TextField
                    readOnly={viewOnly || !canEdit}
                    ref={inputRef}
                    id="ai-tool-title-editor"
                    value={displayedText}
                    onChange={setDocumentTitle}
                    placeholder={t`Name your document...`}
                    aria-label={t`Edit document title`}
                    aria-disabled={!canEdit}
                    style={styles.textField}
                    autoComplete="off"
                    onFocus={() => {
                        if (canEdit) {
                            setEditing(true);
                        }
                    }}
                    onBlur={() => setEditing(false)}
                    // eslint-disable-next-line @khanacademy/ts-no-error-suppressions -- see below
                    // @ts-expect-error these are valid props!
                    size={displayedText ? "1" : undefined}
                    tabIndex={canEdit ? 0 : -1}
                    spellCheck="false"
                />
            </View>
            {!viewOnly && canEdit && (
                <IconButton
                    icon={pencilIcon}
                    kind="tertiary"
                    actionType="neutral"
                    disabled={!canEdit}
                    style={styles.icon}
                    aria-label={t`Edit document title`}
                    onClick={() => {
                        setEditing(true);
                        inputRef.current?.focus();
                    }}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        alignItems: "center",
    },
    static: {
        paddingInlineStart: sizing.size_100,
    },
    sizer: {
        display: "grid",
        alignItems: "center",
        "::after": {
            content: "attr(data-text)",
            visibility: "hidden",
            fontWeight: "bold",
            fontSize: 16,
            whiteSpace: "pre-wrap",
            padding: sizing.size_080,
            border: `${border.width.medium} solid transparent`,
            gridArea: "1 / 2",
        },
    },
    textField: {
        fontWeight: "bold",
        background: "none",
        padding: sizing.size_080,
        border: `${border.width.medium} solid transparent`,
        color: "currentColor",
        gridArea: "1 / 2",
        width: "initial",
    },
    icon: {
        marginInlineStart: sizing.size_0,
        flexShrink: 0,
    },
});
